import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import _isNil from "lodash-es/isNil";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import fetch, { Headers, Response } from 'node-fetch';
import Request from './Request';
import { getLdapDn, getAgency, validateAgency } from '../auth';

/**
 * PostgREST's max-rows on the tenant pgrest pods (PGRST_MAX_ROWS). A single
 * response is silently truncated to this many rows, so site-wide RPC results
 * are read page by page, ordered by id so pages do not overlap.
 */
export const PGREST_PAGE_SIZE = 10000;
/** Columns of getsitevisits the compliance scoring reads (OP-33263). */

export const SITE_VISIT_COLUMNS = 'id,patientnumber,rolegroup_id,status,effectivedate,effectivedatetime_shifted';
/** Columns of getsitecareplans the compliance scoring reads (OP-33263). */

export const SITE_CARE_PLAN_COLUMNS = 'id,patientnumber,rolegroup_id,role_id,discipline,effectivedate,enddate,frequency,period,frequencytext,frequencyfirstpartial,daysofweek,prn,site_id';
export class PimPgRest extends Request {
  constructor({
    url,
    keycloak,
    agency,
    signal
  }) {
    super({
      url,
      signal,
      keycloak
    });

    _defineProperty(this, "keycloak", void 0);

    _defineProperty(this, "commonHeaders", {
      Authorization: '',
      Agency: '',
      'Content-Type': 'application/json',
      Accept: 'application/json'
    });

    _defineProperty(this, "get", async (...args) => {
      const baseOptions = {
        headers: {},
        qs: {}
      };
      const options = typeof args[0] === 'string' ? _objectSpread(_objectSpread({}, baseOptions), {}, {
        api: args[0]
      }) : _objectSpread(_objectSpread({}, baseOptions), args[0]);
      await this.refreshToken(this.keycloak);
      const mergedOptions = this.mergeOptions(options);
      const requestUrl = this.buildRequestUrl(mergedOptions.api, mergedOptions.qs);
      return fetch(requestUrl.href, {
        method: 'GET',
        mode: 'cors',
        signal: this.signal,
        headers: new Headers(_objectSpread(_objectSpread({}, this.commonHeaders), mergedOptions.headers))
      }).then(this.parseJSON);
    });

    _defineProperty(this, "query", async (table, queryParams, schema) => {
      let headers = {};

      if (schema) {
        headers = _objectSpread(_objectSpread({}, headers), {}, {
          'Accept-Profile': schema
        });
      } else {
        // default to the public schema
        headers = _objectSpread(_objectSpread({}, headers), {}, {
          'Accept-Profile': 'public'
        });
      }

      const response = await this.get({
        api: `/api/query/${table}`,
        qs: queryParams,
        headers
      });
      return response;
    });

    _defineProperty(this, "getIdgReviews", async (patientNumber, queryParams = {}) => {
      const response = await this.get({
        api: `/api/idgreviews`,
        qs: queryParams
      });
      return response;
    });

    _defineProperty(this, "getIdgMeetingAgenda", async (siteId, dateFrom, dateTo) => {
      const params = {
        select: 'idgmeeting_id,teamname,teamsubgroup,patientnumber,patient_id,patientfirstname,patientlastname,siteid,patientmanifest_id,displayname,formname,agendastart,agendastop',
        siteid: `eq.${siteId}`
      }; // Meetings whose agenda window overlaps [dateFrom, dateTo]:
      // agendastart <= dateTo AND agendastop >= dateFrom

      if (dateFrom) params.agendastop = `gte.${dateFrom}`;
      if (dateTo) params.agendastart = `lte.${dateTo}`;
      const response = await this.query('idgmeetingagenda_vw', params, 'pimreader');
      return response !== null && response !== void 0 ? response : [];
    });

    _defineProperty(this, "getAllRpcRows", async (fn, qs) => {
      const rows = [];

      for (let offset = 0;; offset += PGREST_PAGE_SIZE) {
        // eslint-disable-next-line no-await-in-loop
        const page = await this.get({
          api: `/api/query/rpc/${fn}`,
          qs: _objectSpread(_objectSpread({}, qs), {}, {
            order: 'id.asc',
            limit: String(PGREST_PAGE_SIZE),
            offset: String(offset)
          }),
          headers: {
            'Accept-Profile': 'pimreader'
          }
        });

        if (!Array.isArray(page)) {
          var _failed$status;

          // Request.parseJSON hands back the raw Response on a non-2xx or
          // non-JSON reply.
          const failed = page || {};
          throw new Error(failed.statusText || `${fn} ${(_failed$status = failed.status) !== null && _failed$status !== void 0 ? _failed$status : 'failed'}`);
        }

        const batch = page;
        rows.push(...batch);
        if (batch.length < PGREST_PAGE_SIZE) return rows;
      }
    });

    _defineProperty(this, "getSiteCarePlans", (siteId, fromDate, toDate) => this.getAllRpcRows('getsitecareplans', {
      p_site_id: siteId,
      p_fromdate: fromDate,
      p_todate: toDate,
      select: SITE_CARE_PLAN_COLUMNS
    }));

    _defineProperty(this, "getSiteVisits", (siteId, fromDate, toDate) => this.getAllRpcRows('getsitevisits', {
      p_site_id: siteId,
      p_fromdate: fromDate,
      p_todate: toDate,
      select: SITE_VISIT_COLUMNS
    }));

    _defineProperty(this, "isPatientOnAdHocIDGAgenda", async patientNumber => {
      const response = await this.query('idgmeetingadhoc_vw', {
        select: 'patientnumber',
        patientnumber: `eq.${patientNumber}`,
        limit: '1'
      }, 'pimreader');

      if (response instanceof Response) {
        return response;
      }

      return Array.isArray(response) && response.length > 0;
    });

    const headerAgency = agency && validateAgency(keycloak.tokenParsed, agency) ? agency : getAgency(getLdapDn(keycloak.tokenParsed));

    if (!headerAgency) {
      // TODO: better error message
      throw new Error(`could not find agency`);
    }

    this.url = new URL(url);
    this.keycloak = keycloak;
    this.commonHeaders.Agency = headerAgency;
    Object.defineProperty(this.commonHeaders, 'Authorization', {
      get() {
        const {
          token
        } = keycloak;
        return `Bearer ${token}`;
      },

      set(newValue) {// need a setter
      }

    });
    this.commonHeaders.Authorization = `Bearer ${keycloak.token}`;
  }

  /**
   * Override method from Request class...
   *
   * For pgrest calls, we need to process the parameters in a particular order.
   * More work may need to be done here, but at the very least, we want to ensure
   * that the 'select' parm is first.
   *
   * We may want to eventually build out a PgRest request builder, but I don't think
   * we have enough information at this time to define the Acceptance Criteria
   * necessary to do that properly.
   */
  setParamsFromQs(queryString) {
    if (queryString) {
      const sortedQuerystring = Object.entries(queryString).sort((a, b) => {
        if (a[0] === 'select') return -1;else if (b[0] === 'select') return 1;
        return 0;
      });
      sortedQuerystring.forEach(([k, v]) => {
        if (!_isNil(v)) {
          this.setParam(k, v);
        }
      });
    }
  }
  /** Build an isolated request URL so concurrent pimreader calls do not share searchParams. */


  buildRequestUrl(api, queryString) {
    const requestUrl = new URL(this.url.origin);
    requestUrl.pathname = this.getPathName(api);

    if (queryString) {
      const sortedQuerystring = Object.entries(queryString).sort((a, b) => {
        if (a[0] === 'select') return -1;
        if (b[0] === 'select') return 1;
        return 0;
      });
      sortedQuerystring.forEach(([k, v]) => {
        if (!_isNil(v)) {
          requestUrl.searchParams.set(k, v);
        }
      });
    }

    return requestUrl;
  }
  /**
   * PostgREST calls share one PimPgRest instance (via dashboard `pim`). The base
   * Request class mutates `this.url.searchParams` and cleanup() only removes keys
   * from the current call's `qs`, so filters from one view (e.g. person_id on
   * rankedpersonroles_vw) can leak into another (e.g. idgmeetingagenda_vw).
   */


  // PgRest carries agency in the Agency header (set at construction), and query
  // params are PostgREST filters/selectors — never inject agency as a URL param.
  // eslint-disable-next-line @typescript-eslint/no-empty-function, class-methods-use-this
  ensureAgencyParam() {}

}
export default PimPgRest;